import { showError } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import {
  getNewSelectedEventTypes,
  recordingRulesEventDataSelector,
  updateEventsObj,
  updateSelectedEvents,
} from './helpers'


const RecordingRulesContext = createContext()

export const RecordingRulesProvider = ({
  children,
  isLoading,
  calendarData,
  eventTypeData,
  miData,
  fetchMoreEvents,
  handleSetIsDirty = () => {},
}) => {
  const dispatch = useDispatch()
  const [eventTypes, setEventTypes] = useState([])
  const [hasEventError, setHasEventError] = useState(false)

  const handleValidateSubmit = useCallback((values) => {
    if (values.length <= 0) {
      dispatch(showError(I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.error_toast')))
      setHasEventError(true)
      return false
    }
    setHasEventError(false)
    return values
  }, [dispatch])

  const handleSubmitRecordingRules = useCallback(() => {
    const oldSelectedEventTypeSet = new Set(miData?.autoRecordV3CalendlyIncludeList)
    const newSelectedEventTypeSet = getNewSelectedEventTypes(
      oldSelectedEventTypeSet,
      eventTypes
    )
    const newEventData = [...newSelectedEventTypeSet]
    return handleValidateSubmit(newEventData)
  }, [miData?.autoRecordV3CalendlyIncludeList, eventTypes, handleValidateSubmit])

  const handleEventTypeSelect = useCallback(
    (eventTypeId, itemId, isChecked) => {
      const newValues = eventTypes.map((eventType) => {
        if (String(eventType.id) === String(eventTypeId)) {
          const newItems = eventType.items.map((item) => {
            if (item.id === itemId) {
              return { ...item, selected: isChecked }
            }
            return item
          })
          return { ...eventType, items: newItems }
        }
        return eventType
      })
      handleSetIsDirty(true)
      setHasEventError(false)
      setEventTypes(newValues)
    },
    [eventTypes, handleSetIsDirty]
  )

  const handleSetAll = useCallback(
    (selected) => {
      const newValues = eventTypes.map((eventV) => {
        const newItems = eventV.items.map((item) => ({ ...item, selected }))
        return { ...eventV, items: newItems }
      })
      setEventTypes(newValues)
      handleSetIsDirty(true)
    },
    [eventTypes, handleSetIsDirty]
  )

  const handleFetchMoreEvents = useCallback(
    async (groupData) => {
      const data = await fetchMoreEvents(groupData)
      if (data.isSuccess) {
        const {
          collection,
          pagination: { currentPage },
        } = data.moreData
        const updatedEventTypes = eventTypes.map((evTSection) => {
          if (evTSection.id === groupData.id) {
            const updatedCollection = updateSelectedEvents(
              collection,
              miData?.autoRecordV3CalendlyIncludeList
            )
            evTSection.items = [...evTSection.items, ...updatedCollection]
            evTSection.currentPage = currentPage
            if (collection.length === 0) {
              evTSection.showButton = false
            } else {
              evTSection.showButton = true
            }
          }
          return evTSection
        })
        setEventTypes(updatedEventTypes)
      }
    },
    [fetchMoreEvents, eventTypes, miData]
  )

  useEffect(() => {
    const eventTypeCollection = recordingRulesEventDataSelector(eventTypeData)
    const updatedEvents = updateEventsObj(eventTypeCollection, miData?.autoRecordV3CalendlyIncludeList)
    setEventTypes(updatedEvents)
  }, [calendarData, eventTypeData, miData])

  // Calculate actual selected count from current UI state (includes both loaded and unloaded selected items)
  const actualSelectedCount = useMemo(() => {
    const oldSelectedSet = new Set(miData?.autoRecordV3CalendlyIncludeList || [])
    const newSelectedSet = getNewSelectedEventTypes(oldSelectedSet, eventTypes)
    return newSelectedSet.size
  }, [miData?.autoRecordV3CalendlyIncludeList, eventTypes])

  const recordingRulesContextValue = useMemo(
    () => ({
      eventTypes,
      hasEventError,
      actualSelectedCount,
      handleSubmitRecordingRules,
      handleEventTypeSelect,
      handleSelectAll: () => handleSetAll(true),
      handleClearAll: () => handleSetAll(false),
      handleFetchMoreEvents,
    }),
    [
      eventTypes,
      hasEventError,
      actualSelectedCount,
      handleSetAll,
      handleEventTypeSelect,
      handleFetchMoreEvents,
      handleSubmitRecordingRules,
    ]
  )

  if (isLoading) return null

  return (
    <RecordingRulesContext.Provider value={recordingRulesContextValue}>
      {children}
    </RecordingRulesContext.Provider>
  )
}

export const useRecordingRulesContext = () => useContext(RecordingRulesContext)
